import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import type {
  NorthArrowMarkerLayer,
  ShapeMarkerFormId,
} from './layoutTypes';

export const SHAPE_MARKER_FORMS: readonly {
  readonly id: ShapeMarkerFormId;
  readonly label: string;
}[] = [
  { id: 'circle', label: 'Circle' },
  { id: 'square', label: 'Square' },
  { id: 'triangle', label: 'Triangle' },
  { id: 'roundedSquare', label: 'Rounded square' },
  { id: 'pin', label: 'Map pin' },
  { id: 'diamond', label: 'Diamond' },
  { id: 'star', label: 'Star' },
  { id: 'cross', label: 'Cross' },
  { id: 'verticalBar', label: 'Vertical bar' },
  { id: 'horizontalBar', label: 'Horizontal bar' },
  { id: 'capsule', label: 'Capsule' },
  { id: 'hexagon', label: 'Hexagon' },
  { id: 'wideHexagon', label: 'Wide hexagon' },
  { id: 'oval', label: 'Oval' },
  { id: 'numberOne', label: 'Number 1' },
  { id: 'circledOne', label: 'Circled number 1' },
  { id: 'corner', label: 'Corner marker' },
];

export const MARKER_FONT_GLYPHS = [
  'N',
  '↑',
  '▲',
  '◆',
  '●',
  '✦',
  '✚',
  '⊙',
] as const;

function FormGeometry({ formId }: { readonly formId: ShapeMarkerFormId }) {
  switch (formId) {
    case 'circle':
      return <circle cx="32" cy="40" r="18" />;
    case 'square':
      return <rect x="15" y="23" width="34" height="34" />;
    case 'triangle':
      return <path d="M32 17 53 59H11Z" />;
    case 'roundedSquare':
      return <rect x="14" y="22" width="36" height="36" rx="7" />;
    case 'pin':
      return <path d="M32 13c-10 0-18 8-18 18 0 14 18 34 18 34s18-20 18-34c0-10-8-18-18-18Zm0 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" />;
    case 'diamond':
      return <path d="M32 13 51 40 32 67 13 40Z" />;
    case 'star':
      return <path d="m32 12 7 19 20 1-16 12 6 20-17-12-17 12 6-20L5 32l20-1Z" />;
    case 'cross':
      return <path d="M27 15h10v20h20v10H37v20H27V45H7V35h20Z" />;
    case 'verticalBar':
      return <rect x="27" y="13" width="10" height="54" />;
    case 'horizontalBar':
      return <rect x="9" y="31" width="46" height="18" />;
    case 'capsule':
      return <rect x="8" y="27" width="48" height="26" rx="13" />;
    case 'hexagon':
      return <path d="M18 14h28l14 26-14 26H18L4 40Z" />;
    case 'wideHexagon':
      return <path d="M14 22h36l11 18-11 18H14L3 40Z" />;
    case 'oval':
      return <ellipse cx="32" cy="40" rx="26" ry="17" />;
    case 'numberOne':
      return <text x="32" y="59" textAnchor="middle" fontSize="48">1</text>;
    case 'circledOne':
      return (
        <>
          <circle cx="32" cy="40" r="24" />
          <text
            x="32"
            y="55"
            textAnchor="middle"
            fontSize="38"
            fill="var(--marker-paper)"
            stroke="none"
          >
            1
          </text>
        </>
      );
    case 'corner':
    default:
      return <path d="M17 13h30v8H25v46h-8Z" />;
  }
}

export function ShapeMarkerArtwork({
  marker,
}: {
  readonly marker: NorthArrowMarkerLayer;
}) {
  if (!marker.visible) return null;
  if (marker.source === 'file' && marker.fileDataUrl) {
    return (
      <image
        href={marker.fileDataUrl}
        x="5"
        y="5"
        width="54"
        height="70"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }
  if (marker.source === 'font') {
    return (
      <text
        x="32"
        y="59"
        textAnchor="middle"
        fontSize="52"
        fill={marker.fillColor ?? 'none'}
        stroke={marker.outlineColor ?? 'none'}
        strokeWidth={marker.outlineWidthPt}
        paintOrder="stroke"
      >
        {marker.fontGlyph}
      </text>
    );
  }

  const scale = marker.respectFrame ? 0.84 : 1;
  return (
    <g
      transform={`translate(32 40) scale(${scale}) translate(-32 -40)`}
      fill={marker.fillColor ?? 'none'}
      stroke={marker.outlineColor ?? 'none'}
      strokeWidth={marker.outlineWidthPt}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect={marker.scaleProportionally ? undefined : 'non-scaling-stroke'}
      style={{ '--marker-paper': tokens.color.canvas.light } as CSSProperties}
    >
      <FormGeometry formId={marker.formId} />
    </g>
  );
}
